import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import type { Document, Manifest } from '../../api/authoring'
import { useCreateComponentMutation } from '../../api/authoring'
import { problemMessage } from '../../api/baseApi'
import { payloadId, useIntent, shown } from '../../api/useIntent'
import { allLinkIds, mintLinkId } from '../../model/link'
import { structuralIntents, structuralPayload, typeTitle } from '../../model/manifest'
import { routes } from '../../model/routes'
import { PRIMARY_MENU, menusInUse, resolveRouting, websiteFields } from '../../model/website'
import { MenuTags } from '../site/MenuTags'

/**
 * Adding a page: one dialog, two commands, and an honest failure.
 *
 * **Adding a page is two commands against two aggregates** — create the
 * Page, then place a route on the Website — and the client used to make
 * an author perform both, in two screens, in the right order, with the
 * second one's listing lagging behind the first. The trigger said "Add
 * Page" and the dialog said "Add a route", which is a seam that should
 * not be visible to anyone who thinks of a page as one thing.
 *
 * **It is not atomic and does not pretend to be.** If the route fails
 * after the page is created, the page exists in the library and this
 * says so and offers the route again — recoverable and visible, which is
 * the property that matters. What would make it atomic is a
 * transactional append, which the promote and detach operations are
 * already waiting on; this is a second call for it rather than a reason
 * to stall.
 *
 * The slug is derived from the title and shown before it is sent, on the
 * same terms the type designer derives a type name: the rule is a
 * spelling convention rather than a decision the author is making.
 */
export function AddPageDialog({
  tenant,
  site,
  open,
  under,
  document,
  manifest,
  onClose,
}: {
  tenant: string
  site: string
  open: boolean
  /** The page link id of the level being added to. Empty is the top. */
  under: string
  document: Document
  manifest: Manifest | undefined
  onClose: () => void
}) {
  const navigate = useNavigate()
  const [create, { isLoading: creating, error: createError, reset }] = useCreateComponentMutation()
  const { send, isLoading: routing, error: routeError } = useIntent(tenant)

  const [title, setTitle] = useState('')
  const [slug, setSlug] = useState('')
  const [description, setDescription] = useState('')
  // A new page joins the primary menu, which is what "add a page" has
  // always meant here. Any other set is a change the author makes on the
  // spot, and the picker offers whatever the site already uses.
  const [menus, setMenus] = useState<string[]>([PRIMARY_MENU])
  // The page that was made when the route that should have followed it
  // failed. It is held so the second half can be retried against the
  // component that already exists, rather than making a second one.
  const [orphan, setOrphan] = useState<Document | null>(null)

  // Cleared when the dialog opens, and only then. The mutation's `reset`
  // is not a stable function — it changes when a request starts — so
  // listing it as a dependency cleared the form the moment Add was
  // pressed, and a refusal arrived to an empty form whose "Try again"
  // had nothing left to try. Read through a ref, it is called without
  // being a reason to run.
  const resetMutation = useRef(reset)
  resetMutation.current = reset
  useEffect(() => {
    if (!open) return
    setTitle('')
    setSlug('')
    setDescription('')
    setMenus([PRIMARY_MENU])
    setOrphan(null)
    resetMutation.current()
  }, [open])

  const typeName = document.type
  // Website has two unkeyed placement intents. Only this one grows the
  // routing tree; `website.not_found_placed` replaces its one child and
  // cannot accept a route's slug or navigation fields.
  const placing = structuralIntents(manifest, typeName).find((intent) => intent.name === 'website.page_added')
  // What a route reaches. A route names a page and the model says which
  // type that is; this client does not.
  const pageType = (manifest?.types ?? []).find((declared) => declared.role === 'page')?.name

  const derived = slug.trim() || slugify(title)
  const busy = creating || routing
  const valid = title.trim() !== '' && derived !== '' && Boolean(placing) && Boolean(pageType)

  const placeRoute = async (page: Document) => {
    if (!placing) return
    const taken = new Set(allLinkIds(document.children))
    const linkId = mintLinkId(derived, taken, 'page')
    const siteRoutes = resolveRouting(document)
    const level = under
      ? (siteRoutes.flatMap(flatten).find((route) => route.route.page === under)?.children ?? [])
      : siteRoutes
    await send(
      site,
      placing.name,
      structuralPayload(
        placing,
        {
          child: { linkId, id: payloadId(tenant, page.id) },
          link: under || undefined,
          position: level.length,
        },
        { slug: derived, title: title.trim(), description: description.trim() || undefined, menus },
      ),
    )
    onClose()
    // Straight to the page that was just made. The site's own listing is
    // already correct — a command answers with the whole updated
    // document — but the author's next move is the words on the page.
    navigate(routes.page(tenant, site, page.id))
  }

  const submit = async () => {
    if (!pageType) return
    try {
      const page = orphan ?? (await create({
        tenant,
        createComponent: { type: pageType, fields: { title: title.trim() } },
      }).unwrap())
      setOrphan(page)
      await placeRoute(page)
    } catch {
      // Both halves report through their own hook's error, which is what
      // the alerts below read. Swallowed here so a rejected command does
      // not become an unhandled rejection.
    }
  }

  const pageWord = pageType ? typeTitle(manifest, pageType).toLowerCase() : 'page'

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{under ? `Add a ${pageWord} underneath` : `Add a ${pageWord}`}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ pt: 1 }}>
          <TextField
            label="What is the page called?"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            disabled={busy}
            autoFocus
            fullWidth
            helperText="This is the title on the page and the name in the menu."
          />

          <TextField
            label="Address"
            value={slug}
            onChange={(event) => setSlug(event.target.value)}
            disabled={busy}
            fullWidth
            placeholder={slugify(title)}
            helperText={
              derived
                ? `Visitors reach it at /${derived}. Leave it alone and it follows the name.`
                : 'The last part of the address, made from the name.'
            }
          />

          <TextField
            label="Description"
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            disabled={busy}
            fullWidth
            multiline
            minRows={2}
            helperText="Optional. Shown with the page's name in menus that have room for it."
          />

          <MenuTags
            value={menus}
            options={menusInUse(websiteFields(document).routing?.nodes)}
            onChange={setMenus}
            disabled={busy}
            helperText="Which menus it appears in. Leave it empty and the page is reachable at its address without being listed."
          />

          {/* Said once, here, because it is the one thing about this
              operation an author has to know and cannot see: the page
              exists the moment this is pressed, and reaches visitors
              with the next release. */}
          <Typography variant="caption" color="text.secondary">
            The page is created straight away and starts empty. Visitors see it once the site is next published.
          </Typography>

          {createError && (
            <Alert severity="error">{problemMessage(createError, 'The page could not be created.')}</Alert>
          )}

          {orphan && routeError && (
            <Alert severity="warning">
              <AlertTitle>The page was created, but it could not be added to the menu</AlertTitle>
              {problemMessage(routeError, 'The menu could not be updated.')} The page itself is safe. Press Try again to
              add it to the menu; a second page will not be created.
            </Alert>
          )}

          {!placing && (
            <Alert severity="warning">
              Pages cannot be added to this site from here.
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Cancel
        </Button>
        <Button variant="contained" onClick={() => submit().catch(shown)} disabled={!valid || busy}>
          {orphan ? 'Try again' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function flatten(route: ReturnType<typeof resolveRouting>[number]): ReturnType<typeof resolveRouting> {
  return [route, ...route.children.flatMap(flatten)]
}

/**
 * A title as an address segment.
 *
 * Derived rather than demanded, and shown before it is sent: the rule is
 * a spelling convention, and making somebody retype "Our Work" as
 * "our-work" teaches them nothing about their own site.
 */
function slugify(raw: string): string {
  return raw
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

